import React, { useState, useRef } from "react";
import "../../../styles/styles.css";
import "../../../../../styles/global.css";
import AccreditationInput from "../../../../AccreditationInput/AccreditationInput";
import AccreditationButton from "../../../../AccreditationButton/AccreditationButton";
import { useAlert } from "../../../../../utils/AlertContext";
import { apiRoutes } from "../../../../../utils/apiRoutes";
import { updateData } from "../../../../../utils/methods";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../Loader/Loader";
import PasswordRequirements from "../../../../PasswordRequirements/PasswordRequirements";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;

const ChangePassword = ({ t }) => {
  const nextButtonRef = useRef(null);
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({
    old_password: "",
    new_password: "",
    confirmed_password: "",
  });

  const [errors, setErrors] = useState({
    old_password: false,
    new_password: false,
    confirmed_password: false,
  });

  const [messages, setMessages] = useState({
    old_password: "",
    new_password: "",
    confirmed_password: "",
    general: "",
  });

  const handleInputChange = (field, value) => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
    setMessages((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const validateAllFields = () => {
    const { old_password, new_password, confirmed_password } = credentials;

    const nextErrors = {
      old_password: false,
      new_password: false,
      confirmed_password: false,
    };

    const nextMessages = {
      old_password: "",
      new_password: "",
      confirmed_password: "",
      general: "",
    };

    // old password required
    if (!old_password) {
      nextErrors.old_password = true;
      nextMessages.old_password = t("UI_OLD_PASSWORD_IS_REQUIRED");
    }

    // new password required + regex
    if (!new_password) {
      nextErrors.new_password = true;
      nextMessages.new_password = t("UI_NEW_PASSWORD_IS_REQUIRED");
    } else if (!PASSWORD_REGEX.test(new_password)) {
      nextErrors.new_password = true;
      nextMessages.new_password = t("UI_PASSWORD_MUST_MEET_REQUIREMENTS");
    }

    // confirm required
    if (!confirmed_password) {
      nextErrors.confirmed_password = true;
      nextMessages.confirmed_password = t(
        "UI_PLEASE_CONFIRM_YOUR_NEW_PASSWORD",
      );
    }

    // match check
    if (
      new_password &&
      confirmed_password &&
      new_password !== confirmed_password
    ) {
      nextErrors.confirmed_password = true;
      nextMessages.confirmed_password = t("UI_PASSWORDS_DO_NOT_MATCH");
    }

    setErrors(nextErrors);
    setMessages(nextMessages);

    const hasError = Object.values(nextErrors).some(Boolean);
    return !hasError;
  };

  const handleChangePassword = async () => {
    const isValid = validateAllFields();
    if (!isValid) return;

    const sendData = {
      oldPassword: credentials.old_password,
      newPassword: credentials.new_password,
    };

    setIsLoading(true);
    try {
      const result = await updateData(
        apiRoutes.changePassword,
        sendData,
        showAlert,
        navigate,
        t,
      );
      if (!result) return;

      showAlert(t("SUCCESSFULLY_SAVED"), "success");
    } catch (error) {
      console.error("API poziv nije uspeo:", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextButtonRef.current?.click();
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
        <div className="flex-direction-column items-center gap-24 width-100 change-password">
          <p className="inria-sans-400 font-24 modal-title">
            {t("CHANGE_PASSWORD")}
          </p>

          <div className="flex-direction-column gap-24 width-100">
            <div className="flex-direction-column gap-24 width-100">
              <div className="flex-direction-column gap-8">
                <AccreditationInput
                  placeholder="*********"
                  className={`change-password ${
                    errors.old_password ? "error" : ""
                  }`}
                  error={errors.old_password}
                  setInputValue={(value) =>
                    handleInputChange("old_password", value)
                  }
                  value={credentials.old_password}
                  label={t("OLD_PASSWORD")}
                  type="password"
                  onKeyDown={onEnter}
                  autoComplete="current-password"
                />
                {messages.old_password ? (
                  <p className="input-error-text">{messages.old_password}</p>
                ) : null}
              </div>

              <div className="flex-direction-column gap-8">
                <AccreditationInput
                  placeholder="*********"
                  className={`change-password ${
                    errors.new_password ? "error" : ""
                  }`}
                  setInputValue={(value) =>
                    handleInputChange("new_password", value)
                  }
                  value={credentials.new_password}
                  label={t("NEW_PASSWORD")}
                  type="password"
                  onKeyDown={onEnter}
                  autoComplete="new-password"
                />
                {messages.new_password && !credentials.new_password ? (
                  <p className="input-error-text">{messages.new_password}</p>
                ) : null}
                <PasswordRequirements password={credentials.new_password} t={t} />
              </div>

              <div className="flex-direction-column gap-8">
                <AccreditationInput
                  placeholder="*********"
                  className={`change-password ${
                    errors.confirmed_password ? "error" : ""
                  }`}
                  setInputValue={(value) =>
                    handleInputChange("confirmed_password", value)
                  }
                  value={credentials.confirmed_password}
                  label={t("CONFIRM_PASSWORD")}
                  type="password"
                  onKeyDown={onEnter}
                  autoComplete="new-password"
                />
                {messages.confirmed_password ? (
                  <p className="input-error-text">
                    {messages.confirmed_password}
                  </p>
                ) : null}
              </div>

              {messages.general ? (
                <p className="input-error-text">{messages.general}</p>
              ) : null}
            </div>

            <AccreditationButton
              type="save"
              title={t("BTN_SAVE")}
              className="login-btn"
              onClick={handleChangePassword}
              ref={nextButtonRef}
              disabled={isLoading}
            />
          </div>
        </div>
    </>
  );
};

export default ChangePassword;
